import { CSSProperties, useEffect, useState, useContext } from "react";
import useCampaignBuilder, { getDefaultConversionEventName } from "Hooks/useCampaignBuilder";
import { clsx, makeKey, styx, isSymphonyUrl } from "modules/Utils";
import { Body2 } from "components/shareable/Typography";
import { useDetectAdBlock } from "adblock-detect-react";
import Colors from "modules/Colors";
import ConnectionStep, { ConnectionStepProps } from 'pages/MarketingPage/Components/StepsSocialMedia/ConnectionStep'
import AccountContainer from 'components/AccountContainer'
import CardSymphony from "elements/CardSymphony";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import Icon from "elements/Icon";
import TabSymphony from "elements/TabSymphony";
import AdConnectionModal from "components/shareable/AdConnectionModal";
import { AdConnectionType, PlaybookType, SYMPHONY_WEBSITE_URL, PlanType } from "modules/Const";
import useFacebookConnections from "Hooks/useFacebookConnections";
import FacebookIcon from "components/svg-icons/social/facebook";
import millify from "millify";
import { CURRENCY_CODE_DATA } from "helpers/currencies";
import useTikTokConnections from "Hooks/useTikTokConnections";
import ButtonSymphony from "elements/ButtonSymphony";
import Banner from "elements/Banner";
import { AdConnectionOptions, DefaultPixelConversionEventNames } from "modules/Types";
import DebugBox from "components/shareable/DebugBox";
import useBrand from "Hooks/useBrand";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import UpgradeToUnlockCTA from "pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA";
import useFacebookConnectionSteps from "Hooks/useFacebookConnectionSteps";
import ModalSymphony from 'elements/ModalSymphony';

const CONNECT_TABS = [
    {
        id: 'custom',
        title: 'Best Setup'
    },
    // {
    //     id: 'express',
    //     title: 'Express Setup'
    // },
]

interface ConnectionsCampaignViewProps {
    className?: string;
    style?: CSSProperties;
    variant?: 'facebook' | 'tiktok';
    allowExpressSetup?: boolean;
    /** Optional title to override the default title. Pass empty string to hide title section. */
    title?: string;
    /** Optional subtitle to override the default subtitle. Pass empty string to hide subtitle. */
    subtitle?: string;
}

const ConnectionsCampaignView = ({
    className,
    style,
    variant = 'facebook',
    allowExpressSetup = false,
    title,
    subtitle,
}: ConnectionsCampaignViewProps) => {
    const {
        usesExpressSetup,
        conversionsEnabled,
        setConversionsEnabled,
        conversionsEventName,
        playbookType,
        linksToPromote,
        setConversionsEventName,
        getDataForSubmission
    } = useCampaignBuilder();

    const { isProBrand, isOnNewSubscriptionPlan, isGrowthBrand } = useBrand();
    const { handleOpenModal } = useContext(UpgradeModalContext);

    const {
        selectedFacebookAdAccount,
        selectedFacebookPixel,
        selectedFacebookPage,
        selectedInstagramPage,
    } = useFacebookConnections()

    const {
        loggedInUser: loggedInTikTokUser,
        isLoggedIn: isTikTokLoggedIn,
        isLoggingIn: isLoggingIntoTikTok,
        login: loginWithTikTok,
        tiktokAdAccount: selectedTikTokAdAccount,
        tiktokAdAccountFunded,
        refreshTikTokAdAccount,
    } = useTikTokConnections()

    const adBlockDetected = useDetectAdBlock();

    const [showAdConnectionsModal, setShowAdConnectionsModal] = useState(false)
    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null)

    /**
     * Manages conversion tracking based on pixel connection and URL type
     * - For Express Setup: Conversions are automatically enabled (Symphony handles pixel)
     * - For Custom Setup: Requires Facebook pixel connection and conversion event selection
     * - For Symphony URLs, automatically enables conversions if conditions are met
     * - For non-Symphony URLs, disables conversions and clears the conversion event
     */
    useEffect(() => {
        const isExpressFlow = usesExpressSetup();
        
        // Express setup: conversions are always enabled through Symphony's pixel
        if (isExpressFlow) {
            // For express setup, auto-set conversion event name based on playbook type if not already set
            if (!conversionsEventName && playbookType) {
                const defaultConversionEventName = getDefaultConversionEventName(playbookType);
                if(defaultConversionEventName) {
                    setConversionsEventName(defaultConversionEventName);
                }
            }
            
            setConversionsEnabled(true);
            return;
        }

        // Custom setup: require a valid Facebook pixel for conversions
        if (!selectedFacebookPixel?.id) {
            setConversionsEnabled(false);
            return;
        }

        // Check if we have both a pixel and conversion event name
        if (selectedFacebookPixel && conversionsEventName) {
            setConversionsEnabled(true);
            return;
        }

        // Check if we have a URL that should have conversions enabled
        if (linksToPromote && linksToPromote.length > 0) {
            const currentUrl = linksToPromote[0]?.url || '';

            // Determine if this is a Symphony URL
            const isSymphonyUrlFlag = isSymphonyUrl(currentUrl);

            // For Symphony URLs with a pixel, enable conversions and set default conversion event
            if (isSymphonyUrlFlag && selectedFacebookPixel) {
                setConversionsEnabled(true);

                // Auto-set conversion event name based on playbook type if not already set
                if (!conversionsEventName && playbookType) {
                    const defaultConversionEventName = getDefaultConversionEventName(playbookType);
                    if(defaultConversionEventName) {
                        setConversionsEventName(defaultConversionEventName);
                    }
                }
                return;
            }

            // For non-Symphony URLs, clear conversion event and disable
            if (!isSymphonyUrlFlag && currentUrl) {
                setConversionsEventName('');
                setConversionsEnabled(false);
                return;
            }
        }

        // Default case for custom setup - disable conversions
        setConversionsEnabled(false);
    }, [selectedFacebookPixel, conversionsEventName, linksToPromote, playbookType, usesExpressSetup]);

    useEffect(() => {
        if (selectedAdConnectionType) {
            setShowAdConnectionsModal(true)
        } else {
            setShowAdConnectionsModal(false)
        }
    }, [selectedAdConnectionType])

    // Get Facebook connection steps from shared hook
    const { 
        connectionSteps: baseFacebookSteps, 
        handlePixelConnectionClick,
        showLogoutModal,
        handleShowLogoutModal,
        handleCancelLogout,
        handleConfirmLogout,
        isLoggingOut
    } = useFacebookConnectionSteps({
        context: 'campaign',
        onConnectionTypeSelected: setSelectedAdConnectionType,
        includePixelStep: true, // Include pixel step, we'll customize the conversion event step separately
        pixelUpgradeCallToAction: 'Unlock Facebook Pixel tracking and advanced conversions with Symphony Pro',
        iconClassName: 'expressSetupStepIcon' // Use campaign-specific icon className
    });

    /**
     * Handles conversion event connection click - checks pro status first
     * Shows upgrade UI for non-pro users, opens connection modal for pro users  
     */
    const handleConversionEventConnectionClick = () => {
        if (!isProBrand) {
            handleOpenModal({
                source: 'Campaign Creation - Facebook Conversion Event',
                highlightedPlan: PlanType.PRO,
                upgradeCallToAction: 'Unlock Facebook Pixel tracking and advanced conversions with Symphony Pro'
            });
            return;
        }
        setSelectedAdConnectionType(AdConnectionType.FACEBOOK_CONVERSION_EVENT);
    };

    const renderTitleAndDescription = () => {
        return adBlockDetected && (
            <CardSymphony
                className="adBlockDetectedContainer"
                title="Please disable your ad blocker for the best experience."
                subtitle="Symphony may not work correctly if you have an ad blocker enabled because we rely on advertising platforms like Facebook to automate marketing. Please disable your ad blocker to ensure your experience works properly."
                isNoPadding
                size="small"
            />
        )
    }

    const renderBestSetup = () => {
        let connectionSteps: ConnectionStepProps[] = []
        switch (variant) {
            case 'facebook':
                // Start with base Facebook connection steps from shared hook
                connectionSteps = [...baseFacebookSteps]

                // Add Facebook icon to the first step (Profile step)
                if (connectionSteps[0]) {
                    connectionSteps[0] = {
                        ...connectionSteps[0],
                        stepIcon: <FacebookIcon />
                    }
                }

                // Check if we need conversion event step
                let allowConversionEventStep = false
                let selectedConversionEventName = conversionsEventName
                let canEditConversionEvent = true
                let conversionEventIcon = 'facebook'
                let stepTitle = 'Conversion Event'
                let isConversionCompleted = Boolean(conversionsEventName)

                if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
                    playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS
                ) {
                    allowConversionEventStep = true
                    selectedConversionEventName = 'Ads will optimize to maximize streams & followers.'
                    canEditConversionEvent = false
                    conversionEventIcon = 'boost'
                    stepTitle = 'Conversion Event'
                    isConversionCompleted = true
                } else if (playbookType === PlaybookType.INCREASE_LINK_CLICKS) {
                    allowConversionEventStep = true
                } else if (playbookType === PlaybookType.META_PRESAVES) {
                    allowConversionEventStep = true
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && isSymphonyUrl(linksToPromote[0]?.url || '')
                        selectedConversionEventName = isSymphonyLink ? 'Ads will optimize to maximize pre-saves.' : undefined
                        canEditConversionEvent = isSymphonyLink ? false : true
                        isConversionCompleted = isSymphonyLink ? true : isConversionCompleted
                    } else {
                        canEditConversionEvent = true
                    }
                    conversionEventIcon = 'boost'
                    stepTitle = 'Pre-Save Conversion Event'
                } else if (playbookType === PlaybookType.META_EMAIL_SIGNUPS) {
                    allowConversionEventStep = true
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && isSymphonyUrl(linksToPromote[0]?.url || '')
                        canEditConversionEvent = isSymphonyLink ? false : true
                        selectedConversionEventName = isSymphonyLink ? 'Ads will optimize to grow signups.' : undefined
                        isConversionCompleted = isSymphonyLink ? true : isConversionCompleted
                    } else {
                        canEditConversionEvent = true
                    }
                    conversionEventIcon = 'mail'
                    stepTitle = 'Email Signup Conversion Event'
                } else if (playbookType === PlaybookType.META_TICKET_SALES) {
                    allowConversionEventStep = true
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && isSymphonyUrl(linksToPromote[0]?.url || '')
                        canEditConversionEvent = isSymphonyLink ? false : true
                        selectedConversionEventName = isSymphonyLink ? 'Ads will optimize to maximize ticket clicks.' : undefined
                        isConversionCompleted = isSymphonyLink ? true : isConversionCompleted
                    } else {
                        canEditConversionEvent = true
                    }
                    conversionEventIcon = 'metaTicketSalesV2'
                    stepTitle = 'Ticket Conversion Event'
                }

                // Add conversion event step only if pixel is connected and conversion events are allowed
                if (allowConversionEventStep && selectedFacebookPixel?.id) {
                    // Force conversion event name for specific playbook types
                    let displayName = selectedConversionEventName;

                    // Override for specific playbook types if it's a Symphony link
                    if (linksToPromote.length > 0) {
                        const isSymphonyLink = linksToPromote[0]?.isValidUrl && isSymphonyUrl(linksToPromote[0]?.url || '');

                        if (isSymphonyLink) {
                            if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
                                playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS) {
                                displayName = 'Ads will optimize to maximize streams & followers.';
                            } else if (playbookType === PlaybookType.META_PRESAVES) {
                                displayName = 'Ads will optimize to maximize pre-saves.';
                            } else if (playbookType === PlaybookType.META_EMAIL_SIGNUPS) {
                                displayName = 'Ads will optimize to grow signups.';
                            } else if (playbookType === PlaybookType.META_TICKET_SALES) {
                                displayName = 'Ads will optimize to maximize ticket clicks.';
                            }
                        }
                    }


                    connectionSteps.push({
                        stepTitle: stepTitle,
                        connectedAccountName: displayName || 'Select the Conversion Event',
                        connectedAccountIcon: <Icon
                            isCircle={!canEditConversionEvent}
                            className="expressSetupStepIcon"
                            icon={conversionEventIcon}
                            iconType={'default'}
                            size={40} />,
                        stepDescription: isProBrand
                            ? 'Choose which conversion event you want to optimize your ads for.'
                            : '(Growth Feature) Unlock advanced conversion event tracking by upgrading to Symphony Pro.',
                        isCompleted: isConversionCompleted,
                        onButtonClick: handleConversionEventConnectionClick,
                        canEdit: canEditConversionEvent,
                        iconContainerColor: isProBrand ? Colors.grey : Colors.purpleLighter,
                        isLastStep: true
                    })

                    // Update the pixel step to not be the last step if we're adding conversion event
                    connectionSteps = connectionSteps.map((step, index) => {
                        if (step.stepTitle === 'Enable Conversions for immediate impact' && index === connectionSteps.length - 2) {
                            return { ...step, isLastStep: false }
                        }
                        return step
                    })
                }
                break
            case 'tiktok':
                let selectedTikTokCurrency = selectedTikTokAdAccount?.currency || 'USD'
                let selectedTikTokCurrencyName = CURRENCY_CODE_DATA[selectedTikTokCurrency]?.name_plural || 'United States Dollar'
                connectionSteps = [
                    {
                        stepTitle: 'TikTok Business Account',
                        connectedAccountName: loggedInTikTokUser?.display_name || 'TikTok Business Account',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={loggedInTikTokUser?.avatar_url || 'tiktok'}
                            iconType={loggedInTikTokUser?.avatar_url ? 'custom' : 'default'}
                            size={40} />,
                        stepDescription: 'Connecting your TikTok for Business account lets Symphony automatically run ads using your selected ad account.',
                        isCompleted: Boolean(isTikTokLoggedIn),
                        isLoading: isLoggingIntoTikTok,
                        onButtonClick: loginWithTikTok
                    },
                    {
                        stepTitle: 'TikTok Ad Account',
                        connectedAccountName: selectedTikTokAdAccount?.name || 'Connect your TikTok Ad Account',
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={'tiktok'}
                            size={40} />,
                        stepDescription: 'Your TikTok ad account is where you want Symphony to publish your ads. You\'ll be billed based on the billing method in your ad account.',
                        isCompleted: Boolean(selectedTikTokAdAccount),
                        onButtonClick: () => {
                            setSelectedAdConnectionType(AdConnectionType.TIKTOK_AD_ACCOUNT)
                        },
                        connectedAccountDescriptions: selectedTikTokAdAccount ? [
                            `$${millify(Number(selectedTikTokAdAccount.balance) || 0)} Balance`,
                            `This ad account uses the currency ${selectedTikTokCurrencyName} (${selectedTikTokCurrency}).`
                        ] : undefined,
                        canEdit: true
                    },
                    {
                        stepTitle: 'Account Funding',
                        stepDescription: 'To ensure your ads run without interruptions, you\'ll need to ensure your TikTok Ad Account has funds. We recommend keeping at least $20 USD in your account.',
                        connectedAccountName: `Ad Account Spending Balance`,
                        connectedAccountIcon: <Icon
                            className="expressSetupStepIcon"
                            icon={'tiktok'}
                            size={40} />,
                        rightSideContent: selectedTikTokAdAccount ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Body2>
                                    ${millify(Number(selectedTikTokAdAccount.balance) || 0)}
                                </Body2>
                                <ButtonSymphony
                                    className="refreshBalanceButton"
                                    variant="text"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        refreshTikTokAdAccount(true);
                                    }}
                                >
                                    Refresh
                                </ButtonSymphony>
                            </div>
                        ) : undefined,
                        isCompleted: tiktokAdAccountFunded,
                        onButtonClick: () => {
                            if (selectedTikTokAdAccount) {
                                window.open(`https://business.tiktok.com/manage/payment/v2?org_id=${selectedTikTokAdAccount.owner_bc_id}`, '_blank');
                            }
                        },
                        canEdit: true,
                        isLastStep: true
                    }
                ]
                break
        }

        return (
            <div className="bestSetupContainer">
                {connectionSteps.map((step, index) => (
                    <ConnectionStep
                        key={makeKey(step.stepTitle, index)}
                        stepTitle={step.stepTitle}
                        connectedAccountName={step.connectedAccountName}
                        connectedAccountIcon={step.connectedAccountIcon}
                        stepDescription={step.stepDescription}
                        isCompleted={step.isCompleted}
                        onButtonClick={step.onButtonClick}
                        rightSideContent={step.rightSideContent}
                        stepIcon={step.stepIcon}
                        thumbnail={step.thumbnail}
                        iconContainerColor={step.iconContainerColor}
                        disabledButton={step.disabledButton}
                        errorText={step.errorText}
                        tooltip={step.tooltip}
                        canEdit={step.canEdit}
                        buttonText={step.buttonText}
                        connectedAccountDescriptions={step.connectedAccountDescriptions}
                        isLoading={step.isLoading}
                        isLastStep={index === connectionSteps.length - 1}
                    >
                    </ConnectionStep>
                ))}

                <AdConnectionModal
                    isOpen={showAdConnectionsModal}
                    onClose={() => setSelectedAdConnectionType(null)}
                    connectionType={selectedAdConnectionType || undefined}
                />

                <ModalSymphony
                    className="facebookLogoutConfirmationModal"
                    isOpen={showLogoutModal}
                    onClose={handleCancelLogout}
                    title="Are you sure you want to log out?"
                    subtitle="You won't be able to view Meta campaign data until you re-log back in, and your ad account, linked pages, and pixel will also be removed."
                    headerIcon="facebook"
                    size="small"
                    onClickButtonLeft={handleCancelLogout}
                    buttonLeftText="Nevermind"
                    buttonLeftVariant="text"
                    onClickButtonRight={handleConfirmLogout}
                    buttonRightText="Yes, log out"
                    buttonRightVariant="outlined"
                    buttonRightColor="red"
                    buttonRightIsLoading={isLoggingOut}
                    buttonRightDisabled={isLoggingOut}
                />
            </div>
        )
    }

    // NOTE: render()
    return (
        <div
            className={clsx("connectionsCampaignViewComponent", className)}
            style={styx(sty.connectionsCampaignViewComponent, style)}
        >
            {renderTitleAndDescription()}
            <div className="connectionStepsContainer" style={{
                marginTop: 24
            }}>
                {renderBestSetup()}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    connectionsCampaignViewComponent: {
        width: '100%',
    },
    expressSetupContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    },
    stepper: {
        padding: 0
    },
    errorContainer: {
        display: 'flex',
        gap: 12,
        marginTop: 4,
        borderRadius: 8,
        padding: '16px 24px',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: Colors.PAPER_ERROR_CONTAINER,
    },
    errorContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    }
}

export default ConnectionsCampaignView;